import { useEffect, useMemo, useState } from 'react';
import {
  getUserAuthMetadata,
  subscribeToUsers,
  type AuthMetadata,
} from '../../services/firebase';
import type { FirestoreUser } from '../../types';

interface InactiveUserPickerProps {
  /** Called with the resolved uid list whenever the threshold or data changes. */
  onChange: (uids: string[]) => void;
}

const THRESHOLDS = [30, 60, 90, 180] as const;

/**
 * Picks users who have not been active for N days — the "people who aren't
 * using the app" audience.
 *
 * ── Which field counts as "active" ──────────────────────────────────────────
 *
 * `lastRefreshTime` from Firebase Auth, not `lastSignInTime` and definitely not
 * the `lastLoginAt` field on the user doc.
 *
 *   lastLoginAt      Dead. Nothing in the mobile app or in Cloud Functions has
 *                    ever written it; it exists on the type and renders blank.
 *                    Filtering on it would select the entire user base.
 *   lastSignInTime   Real, but it is the last time credentials were entered.
 *                    Firebase refreshes tokens silently, so someone who has
 *                    used the app daily for a year without signing out still
 *                    shows a sign-in from a year ago. It would report your most
 *                    engaged users as dormant.
 *   lastRefreshTime  Last token refresh. Moves whenever the app actually runs,
 *                    which is the closest thing to "opened the app" that exists
 *                    without new instrumentation.
 *
 * A user with no `lastRefreshTime` at all is EXCLUDED rather than treated as
 * infinitely dormant. Absent means the Auth account predates the field or was
 * not returned — an unknown, and mailing an unknown as though it were a
 * confirmed absence is how a re-engagement campaign turns into a complaint.
 */
export function InactiveUserPicker({ onChange }: InactiveUserPickerProps) {
  const [days, setDays] = useState<number>(60);
  const [users, setUsers] = useState<FirestoreUser[]>([]);
  const [metadata, setMetadata] = useState<Record<string, AuthMetadata>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeToUsers(setUsers);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (users.length === 0) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getUserAuthMetadata(users.map((u) => u.uid))
      .then((m) => {
        if (!cancelled) setMetadata(m);
      })
      .catch((err) => {
        console.error('Failed to load auth metadata:', err);
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to load activity data',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [users]);

  const { matches, unknownCount } = useMemo(() => {
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
    const out: FirestoreUser[] = [];
    let unknown = 0;

    for (const u of users) {
      // Closed accounts are the server's job to drop too, but excluding them
      // here keeps the count on screen honest.
      if (u.accountStatus === 'deleted' || u.accountStatus === 'pendingDeletion') {
        continue;
      }
      const raw = metadata[u.uid]?.lastRefreshTime;
      if (!raw) {
        unknown += 1;
        continue;
      }
      const t = new Date(raw).getTime();
      if (Number.isFinite(t) && t < cutoff) out.push(u);
    }

    return { matches: out, unknownCount: unknown };
  }, [users, metadata, days]);

  useEffect(() => {
    onChange(matches.map((u) => u.uid));
  }, [matches, onChange]);

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-3">
        <label className="text-sm text-gray-700">No app activity in</label>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-brand-500"
        >
          {THRESHOLDS.map((d) => (
            <option key={d} value={d}>
              {d} days
            </option>
          ))}
        </select>
      </div>

      {loading && (
        <p className="text-sm text-gray-500">Loading activity data…</p>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-900">
            <span className="font-semibold">{matches.length}</span> user
            {matches.length === 1 ? '' : 's'} match
            {matches.length === 1 ? 'es' : ''}.
          </p>
          {unknownCount > 0 && (
            <p className="text-xs text-gray-500">
              {unknownCount} user{unknownCount === 1 ? '' : 's'} excluded — no
              activity data recorded, which is unknown rather than inactive.
            </p>
          )}
          <p className="text-xs text-gray-500">
            Consent is applied on top of this by the server, so the number
            actually emailed will usually be lower.
          </p>
        </>
      )}
    </div>
  );
}
